import { useCallback, useEffect, useMemo, useRef } from "react";

import { DropdownOption, useDebounceFn } from "../../..";
import { DateValueType } from "../../../DatepickerV2";
import { useIsMobile } from "../../../hooks";
import { useClientPageAnalyticsContext } from "../../Common/providers/ClientPageAnalyticsProvider";
import { ReturnedTranslatedContentClientsPageHook } from "../../types";
import { formatClientTrackingDate } from "../../utils";
import { useClientFundedNotTradedReportContext } from "../providers/ClientFundedNotTradedProvider";
import { DesktopFilters } from "./DesktopFilters";
import { MobileFilters } from "./MobileFilters";
import { MobileFiltersV2 } from "./MobileFiltersV2";
import { ClientFNTTableFilterSubProps } from "./types";

type ClientFundedNotTradedReportFiltersProps = {
  translations: ReturnedTranslatedContentClientsPageHook;
  isMobileBottomSheetEnable?: boolean;
};

export const ClientFundedNotTradedReportFilters = ({
  translations,
  isMobileBottomSheetEnable,
}: ClientFundedNotTradedReportFiltersProps) => {
  const analytics = useClientPageAnalyticsContext();

  const {
    state: { dateRange, selected },
    options: { rebateAccountIDs: rebateAccountOptions },
    actions,
    runResource,
  } = useClientFundedNotTradedReportContext();
  const isMobile = useIsMobile();

  const rebateAccountIDsRef = useRef<DropdownOption[]>(rebateAccountOptions);

  useEffect(() => {
    if (
      rebateAccountIDsRef.current &&
      rebateAccountIDsRef.current.length === 0 &&
      rebateAccountOptions.length > 0
    ) {
      actions.setSelected(
        "rebateAccountIDs",
        rebateAccountOptions.map((o) => o.value),
      );
    }
  }, [actions, rebateAccountOptions]);

  const [handleSearchInput] = useDebounceFn((value: string) => {
    actions.setSearchQuery(value);
    analytics?.trackTableSearchFieldChangeEvent("Funded not traded", value);
  }, 1000);

  const handleDateRangePicker = useCallback(
    (value: DateValueType) => {
      if (
        formatClientTrackingDate(value?.startDate) ===
          formatClientTrackingDate(dateRange.from) ||
        formatClientTrackingDate(value?.endDate) ===
          formatClientTrackingDate(dateRange.to)
      ) {
        analytics?.trackDateRangeFilterEvent(
          formatClientTrackingDate(value?.startDate),
          formatClientTrackingDate(value?.endDate),
          "Funded not traded",
        );
      }

      actions.setDateRange({
        from: value?.startDate ?? null,
        to: value?.endDate ?? null,
      });
    },
    [actions, analytics, dateRange.from, dateRange.to],
  );

  const handleRebateAccountsDropdown = useCallback(
    (options: DropdownOption[]) => {
      const newRebateAccountIDs = options.map((o) => o.value);
      if (
        JSON.stringify(newRebateAccountIDs) !==
        JSON.stringify(selected.rebateAccountIDs)
      ) {
        analytics?.trackTableRebateSelectEventV2(
          "Funded not traded",
          newRebateAccountIDs,
        );
      }

      actions.setSelected("rebateAccountIDs", newRebateAccountIDs);
    },
    [actions, analytics, selected.rebateAccountIDs],
  );

  const [debounceDateTooltipTrack] = useDebounceFn((isMobile: boolean) => {
    analytics?.trackClientDateTooltipEvent(isMobile, "Funded not traded");
  }, 5000);

  const filterConfig = useMemo<ClientFNTTableFilterSubProps>(
    () => ({
      handleDateRangePicker,
      handleExportButton: () => runResource("csv"),
      handleRebateAccountsDropdown,
      handleSearchInput,
      translations,
      onDateTooltipHover: debounceDateTooltipTrack,
    }),
    [
      handleDateRangePicker,
      handleRebateAccountsDropdown,
      handleSearchInput,
      translations,
      debounceDateTooltipTrack,
      runResource,
    ],
  );
  if (isMobile && isMobileBottomSheetEnable) {
    return <MobileFiltersV2 handleSearchInput={handleSearchInput} />;
  }

  return isMobile ? (
    <MobileFilters {...filterConfig} />
  ) : (
    <DesktopFilters {...filterConfig} />
  );
};
